import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE, buildCanonical, ROUTE_SEO, type RouteSEO } from "@/lib/seo-config";

interface SEOProps {
  /** Override page title; falls back to ROUTE_SEO[pathname]?.title or SITE.defaultTitle */
  title?: string;
  description?: string;
  keywords?: string;
  /** Absolute URL or path; auto-resolved against SITE.url */
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  /** Optional JSON-LD object(s) injected as <script type="application/ld+json"> */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

/** Marker attribute so we only manage tags we created. */
const MANAGED_ATTR = "data-seo-managed";

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

function upsertLink(rel: string, href: string, extra: Record<string, string> = {}) {
  const selector = `link[rel="${rel}"]${
    extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ""
  }`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute(MANAGED_ATTR, "true");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
}

export function SEO(props: SEOProps) {
  const { pathname } = useLocation();

  useEffect(() => {
    const route: RouteSEO | undefined = ROUTE_SEO[pathname];
    const title = props.title || route?.title || SITE.defaultTitle;
    const description = props.description || route?.description || SITE.defaultDescription;
    const keywords = props.keywords || route?.keywords || SITE.defaultKeywords;
    const noindex = props.noindex ?? route?.noindex ?? false;
    const ogImage = props.ogImage || SITE.defaultOgImage;
    const canonical = props.canonical
      ? props.canonical.startsWith("http")
        ? props.canonical
        : buildCanonical(props.canonical)
      : buildCanonical(pathname);

    document.title = title;

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });
    upsertMeta('meta[name="googlebot"]', {
      name: "googlebot",
      content: noindex ? "noindex, nofollow" : "index, follow",
    });

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE.name,
    });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: SITE.locale });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    upsertMeta('meta[name="twitter:site"]', {
      name: "twitter:site",
      content: SITE.twitterHandle,
    });

    // Canonical
    upsertLink("canonical", canonical);

    // JSON-LD (route-specific). Replaces any previously injected page-level JSON-LD.
    document.head
      .querySelectorAll(`script[${MANAGED_ATTR}="ld"]`)
      .forEach((n) => n.remove());
    if (props.jsonLd) {
      const blocks = Array.isArray(props.jsonLd) ? props.jsonLd : [props.jsonLd];
      blocks.forEach((block) => {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute(MANAGED_ATTR, "ld");
        script.text = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }
  }, [
    pathname,
    props.title,
    props.description,
    props.keywords,
    props.canonical,
    props.ogImage,
    props.noindex,
    props.jsonLd,
  ]);

  return null;
}

export default SEO;
